import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { DismissReason } from "@coachbrain/contracts";
import { Card, Chip, LayerChip, SectionLabel, Columns } from "@coachbrain/ui";
import { CoachShell, ErrorBox, Loading } from "../components/common";
import { useLoad } from "../lib/hooks";
import { en } from "../i18n/en";

export function DismissSheet({ onClose, onPick }: { onClose: () => void; onPick: (r: DismissReason | null) => void }) {
  const t = en.overview.reasons;
  return (
    <div className="cb-sheet" role="dialog" aria-modal="true" aria-label={en.challenges.dismissTitle} onClick={onClose}>
      <div className="cb-sheet-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cb-h3">{en.challenges.dismissTitle}</div>
        <div className="cb-inline-actions">
          {(["already_planned", "not_appropriate", "dont_believe", "later"] as DismissReason[]).map((r) => <Chip key={r} tone="navy" onClick={() => onPick(r)}>{t[r]}</Chip>)}
        </div>
        <button type="button" className="cb-link-button" onClick={() => onPick(null)}>{en.challenges.hideOnly}</button>
        <button type="button" className="cb-btn cb-btn-outline" onClick={onClose}>{en.common.cancel}</button>
      </div>
    </div>
  );
}

export function ChallengesRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { api, data, error, reload, setData } = useLoad((a) => a.challenges(id), [id]);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data || !api) return <CoachShell><Loading /></CoachShell>;
  const t = en.challenges;
  const refresh = async () => setData(await api.challenges(id));
  return (
    <CoachShell wide title={t.title} subtitle={`${data.open.length} open · ${data.dismissed.length} dismissed`} right={<button type="button" className="cb-link-button" style={{ color: "#C9D2DE" }} onClick={() => void navigate({ to: `/clients/${id}` })}>{en.common.back}</button>}>
      <SectionLabel className="cb-page-label">{t.open}</SectionLabel>
      {data.open.length === 0 ? <Card><span className="cb-muted">{t.none}</span></Card> : null}
      <Columns kind="cards">
      {data.open.map((c, i) => (
        <Card key={c.id} tone="provisional">
          <SectionLabel tone="violet" right={<LayerChip layer={c.layer} />}>{en.history.challenge(i + 1, data.open.length)}</SectionLabel>
          <div className="cb-h2">{c.questions}</div>
          <div className="cb-small"><strong>{t.why}</strong> {c.whyNow}</div>
          <div className="cb-body cb-text-violet">{en.history.settle} {c.settles}</div>
          {c.alternativeView ? <div className="cb-small cb-muted"><strong>{t.alt}</strong> {c.alternativeView}</div> : null}
          <div className="cb-tap-row">
            <button type="button" className="cb-tap cb-tap-primary" onClick={async () => { await api.challengeAddToToday(c.id); await refresh(); }}>{en.history.addToToday}</button>
            <button type="button" className="cb-tap" onClick={async () => { await api.challengeAskClient(c.id); await refresh(); }}>{en.history.ask("Fred")}</button>
            <button type="button" className="cb-tap" onClick={() => setDismissing(c.id)}>{en.history.dismiss}</button>
          </div>
        </Card>
      ))}
      </Columns>
      {data.dismissed.length ? <button type="button" className="cb-link-button" onClick={() => setShowDismissed((s) => !s)}>{t.dismissed} ({data.dismissed.length})</button> : null}
      {showDismissed ? <Columns kind="cards">{data.dismissed.map((c) => (
        <Card key={c.id} style={{ opacity: 0.75 }}>
          <div className="cb-h3">{c.questions}</div>
          <div className="cb-small cb-muted">Dismissed{c.dismissal?.reasoned ? ` · ${en.overview.reasons[c.dismissal.reason]}` : " · no reason"}{c.suppressedUntil ? ` · suppressed for 30 days` : ""}</div>
        </Card>
      ))}</Columns> : null}
      {dismissing ? <DismissSheet onClose={() => setDismissing(null)} onPick={async (r) => { await api.dismissChallenge(dismissing, r ? { reason: r } : null); setDismissing(null); await refresh(); }} /> : null}
    </CoachShell>
  );
}
